import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DialogLogo } from './DialogLogo';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { Dialog, DialogDescription, DialogTitle, type DialogProps } from '~/components/ui/LegacyDialog';
import { useLogin } from '~/lib/login';
import type { UserData } from '~/types/user';

export function ConnectToGithubDialog({
  open,
  onSuccess,
  onClose,
  user,
}: {
  open: boolean;
  onSuccess: () => void;
  onClose: DialogProps['onClose'];
  user?: UserData;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={600}>
      {open && <ConnectToGithubDialogInner user={user} onSuccess={onSuccess} />}
    </Dialog>
  );
}

function ConnectToGithubDialogInner({ onSuccess, user }: { user?: UserData; onSuccess: () => void }) {
  const { login } = useLogin();
  const [githubRequest, setGithubRequest] = useState(false);
  const abortedRef = useRef(false);

  useEffect(() => {
    return () => {
      abortedRef.current = true;
    };
  }, []);

  async function handleLogin() {
    setGithubRequest(true);

    const scope = user?.featureFlags.githubOrgs ? 'repo,read:org' : 'repo';

    if (await login(undefined, 'github', { bolt_oauth_scopes: scope }, true)) {
      onSuccess();
    } else {
      toast.error('Failed to connect to GitHub');
    }
  }

  return (
    <>
      <VisuallyHidden>
        <DialogTitle>Connect to GitHub</DialogTitle>
      </VisuallyHidden>
      <DialogDescription noPadding>
        <DialogLogo />

        <div className="px-5">
          <h1 className="text-center text-lg font-semibold">Connect to GitHub</h1>
          <div className="flex flex-col items-center w-full pt-2 pb-5 text-center">
            <span className="mb-6 max-w-80% text-bolt-elements-text-secondary">
              To get started, log in to GitHub with your account or create one.
            </span>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center">
                <LegacyButton size="lg" variant="primary" onClick={handleLogin} disabled={githubRequest}>
                  {githubRequest ? 'Connecting with GitHub' : 'Log in to GitHub'}
                </LegacyButton>
              </div>
              {githubRequest && (
                <span className="block text-xs text-bolt-elements-text-secondary">
                  Login with GitHub in the other tab and authorize Bolt to continue
                </span>
              )}
            </div>
          </div>
        </div>
      </DialogDescription>
    </>
  );
}
